import { Acabados, Item, Node, Textures, Zone } from '@types'
import getStandardMaterial from '@utils/conversion/getStandardMaterial'
import { radians } from '@utils/generalMaths/geometry'
import {
  applyTextureQuality,
  cloneCachedTexture,
  rememberCachedTexture,
} from '@utils/sceneObjects/textureCache'
import { configureFabricDiffuseSampling } from '@utils/sceneObjects/fabricTextureQuality'
import {
  applyZoneMaterialToMesh,
  applyZoneDetailMapToMesh,
} from '@utils/sceneObjects/resolvePbrMaterial'
import {
  isConservativeFabricTexture,
  resolveTextureTiling,
} from '@utils/sceneObjects/resolveTextureTiling'
import { useAppStore } from '@store'
import * as THREE from 'three'
import { Group, Mesh, RepeatWrapping, TextureLoader } from 'three'

// Cargador de texturas global para reutilización
const loader = new TextureLoader()
// Control de concurrencia por mesh/zona para evitar que callbacks viejos pisen cambios nuevos.
const textureLoadVersionByNode = new Map<string, number>()

// Interfaz para los elementos del grupo contiguo
interface ContiguousFurnitureItem {
  id: string
  catalogID: string
  position: { x: number; y: number; z: number }
  width: number
  height: number
  thickness: number
  rotation: number
  orderIndex: number
  type: string
  description: string
}
// Interfaz para el resultado del análisis de muebles contiguos
interface ContiguousAnalysisResult {
  groups: ContiguousFurnitureItem[][]
  totalGroups: number
  totalItemsInGroups: number
}
// Función exportada para llamar desde componentes con acceso al store
export function analyzeContiguousFurniture(getContiguousItems: () => ContiguousAnalysisResult) {
  try {
    const result = getContiguousItems()
    return result
  } catch (error) {
    return null
  }
}
export function handleNodeMorphs(node: Node, textures: Item['morphs']) {
  if (node.morphTargetDictionary) {
    Object.keys(node.morphTargetDictionary).map((morph, morphIndex) => {
      textures.forEach((property) => {
        if (morph === property.name) {
          if (property.max !== property.min) {
            let value: number

            // Si el valor está dentro de los límites, calcular normalmente
            if (property.default >= property.min && property.default <= property.max) {
              value = (property.default - property.min) / (property.max - property.min)
            }
            // Si el valor supera el máximo, aplicar al máximo (1.0) - el CORTEKIT se encargará del resto
            else if (property.default > property.max) {
              value = 1.0
            }
            // Si el valor es menor que el mínimo, aplicar al mínimo (0.0)
            else {
              value = 0.0
            }

            if (property.reversed) {
              node.morphTargetInfluences![morphIndex] = 1 - value
            } else {
              node.morphTargetInfluences![morphIndex] = value
            }
          }
        }
      })
    })
  }
  // Manejo especial para morph de Regleta en meshes específicos
  if (
    node.name &&
    (node.name.includes('Mesh004') ||
      node.name.includes('regleta') ||
      node.name.includes('Regleta'))
  ) {
    const regletaMorph = textures?.find((morph) => morph.name === 'Regleta')
    if (regletaMorph && node.morphTargetDictionary && 'Regleta' in node.morphTargetDictionary) {
      const morphIndex = node.morphTargetDictionary['Regleta']
      if (morphIndex !== undefined && node.morphTargetInfluences) {
        const value =
          (regletaMorph.default - regletaMorph.min) / (regletaMorph.max - regletaMorph.min)
        node.morphTargetInfluences[morphIndex] = Math.max(0, Math.min(1, value))
      }
    }
  }
}
function findMatchingAcabado(materialName: string, acabados: Acabados[]): Acabados | undefined {
  let acabado = acabados.find((acabado) => acabado.zona === materialName)
  if (!acabado) {
    acabado = acabados.find((acabado) => {
      const cleanMaterialName = materialName.replace(/[0-9]/g, '').trim()
      const cleanZonaName = acabado.zona.replace(/[0-9]/g, '').trim()
      return cleanMaterialName.includes(cleanZonaName) || cleanZonaName.includes(cleanMaterialName)
    })
  }
  return acabado
}
export function getZonesFromGLB(
  children: Node[],
  acabados: Acabados[],
  extraInfo?: { zonas: { idAcabadoUnidad: number; zona: string }[] },
) {
  if (!acabados || !children) return { zones: {}, textures: [] }
  const textures: Textures = []
  let index = 0
  const zones: Zone = {}
  children.forEach((child) => {
    child.traverse((node) => {
      if (node instanceof Group) {
        const { position, scale, rotation } = node
        let { name } = node
        if (typeof name === 'number') {
          name = String(name)
        }
        zones[name] = {
          name,
          scale,
          position,
          rotation,
          index: 0,
        }
      }
    })
  })
  children.forEach((child) => {
    child.traverse((node) => {
      if (node instanceof Mesh) {
        let { name: nodeName, position, scale, rotation } = node
        if (typeof nodeName === 'number') {
          nodeName = String(nodeName)
        }
        const { name: materialName } = node.material
        const acabado = findMatchingAcabado(materialName, acabados)
        if (!acabado) {
          console.warn(`No acabado found for material: ${materialName}`)
          return
        }
        index++
        const materials = getStandardMaterial(acabado.texturas)
        const linkedTextureID =
          extraInfo && extraInfo.zonas
            ? extraInfo.zonas?.find((zoneName) => acabado.descripcion === zoneName.zona)
            : { idAcabadoUnidad: -1, zona: '' }
        const textureId = `${nodeName}_${acabado.idAcabadoUnidad}`
        textures.push({
          id: materials.id,
          searchID: materials.searchID,
          zoneName: nodeName,
          zone: acabado.zona,
          name: materials.name,
          url: materials.url,
          zoneDisplayName: acabado.descripcion,
          groupID: materials.groupID,
          linkedTextureID: linkedTextureID?.idAcabadoUnidad,
          modelo: acabado.modelo || acabado.descripcion,
          acabado: acabado.acabado || acabado.descripcion,
          idAcabadoUnidadModeloAcabado: textureId,
        })
        zones[nodeName] = {
          position,
          scale,
          rotation,
          name: nodeName,
          material: getStandardMaterial(
            acabado.texturas,
            acabado.acabado || acabado.descripcion,
            acabado.modelo || acabado.descripcion,
          ),
          index: index - 1,
          idAcabadoUnidadModeloAcabado: textureId,
        }
      }
    })
  })
  return { zones, textures }
}
export function handleNodeTextures(
  node: Node & { name: keyof Zone },
  zones: Zone,
  textures: Textures,
  repeatX: number,
  repeatY: number,
  textureRotation: number,
  paginaPdf: number | undefined,
  itemHeight: number,
  itemWidth: number,
  itemThickness: number,
  getContiguousItems: (() => ContiguousAnalysisResult) | undefined,
  fatherId: string | undefined = undefined,
  universal: boolean,
  itemId?: string,
) {
  const invalidateR3F = () => {
    try {
      const r3f = typeof window !== 'undefined' ? (window as any).r3f : undefined
      if (r3f?.invalidate) {
        r3f.invalidate()
        requestAnimationFrame(() => {
          if (r3f?.invalidate) r3f.invalidate()
        })
      }
    } catch {
      // noop
    }
  }
  let newTexture
  if (textures.length > 0) {
    textures?.forEach((texture: Textures[number]) => {
      if (texture.zoneName === node.name) {
        const nodeLoadKey = `${itemId || 'no-item'}::${node.uuid}::${String(node.name)}`
        const nextVersion = (textureLoadVersionByNode.get(nodeLoadKey) || 0) + 1
        textureLoadVersionByNode.set(nodeLoadKey, nextVersion)

        // Capturar la URL esperada para verificar en el callback
        // Asegurar que la URL sea completa y no tenga parámetros de query
        const cleanUrl = texture.url?.split('?')[0] || texture.url
        const expectedUrl = cleanUrl
        // const expectedTextureId = texture.searchID

        // Construir la URL completa para cargar la textura
        // Añadir ?not-from-cache-please solo al cargar, no al guardar
        const loadUrl = cleanUrl + '?not-from-cache-please'

        const applyLoadedTexture = (loadedTexture: THREE.Texture) => {
          // Si existe una carga más reciente para este mesh/zona, descartamos esta.
          const currentVersion = textureLoadVersionByNode.get(nodeLoadKey)
          if (currentVersion !== nextVersion) {
            return
          }
          if (itemId) {
            const store = useAppStore.getState()
            const currentItem = store.items.get(itemId) || store.areas.get(itemId)
            const currentTextureForZone = currentItem?.textures?.find(
              (t: any) => t.zoneName === node.name,
            )

            const currentCleanUrl =
              currentTextureForZone?.url?.split('?')[0] || currentTextureForZone?.url
            if (currentTextureForZone && currentCleanUrl !== expectedUrl) {
              return
            }
          }

          loadedTexture.wrapS = RepeatWrapping
          loadedTexture.wrapT = RepeatWrapping
          loadedTexture.flipY = false
          // Mejorar calidad de texturas para renderizado más realista
          applyTextureQuality(loadedTexture)
          // console.log(texture.idAcabadoUnidadModeloAcabado, texture.zone, texture.zoneDisplayName)
          const isCountertop = texture.zone === 'Encimera' || texture.zoneDisplayName === 'Encimera'
          const isFrontal = texture.zone === 'Frontal' || texture.zoneDisplayName === 'Frontal'
          const isHorno =
            (typeof texture.zone === 'string' && !isNaN(Number(texture.zone))) ||
            (typeof texture.zoneDisplayName === 'string' && !isNaN(Number(texture.zoneDisplayName)))
          const isSpecialCounterTop =
            isCountertop && texture.idAcabadoUnidadModeloAcabado === 'Box001_15054'
          const isSpecialCounterTop2 =
            isCountertop && texture.idAcabadoUnidadModeloAcabado === 'Box001_27761'
          let offsetU = 0
          let offsetV = 0

          const isFabricLike = isConservativeFabricTexture(texture, itemId)

          if (universal) {
            loadedTexture.offset.set(0, 0)
            loadedTexture.rotation = 0
            loadedTexture.repeat.set(1, 1)
            loadedTexture.needsUpdate = true

            let zoneMaterial = zones[node.name]?.material
            if (itemId) {
              const store = useAppStore.getState()
              const currentItem = store.items.get(itemId) || store.areas.get(itemId)
              const currentZoneMaterial = currentItem?.zones?.[node.name]?.material
              if (currentZoneMaterial) {
                zoneMaterial = currentZoneMaterial
              }
            } else {
              zoneMaterial = zones[node.name]?.material
            }

            const newMaterial = node.material.clone()

            newMaterial.map = loadedTexture
            applyZoneMaterialToMesh(newMaterial, zoneMaterial, {
              hasTextureMap: true,
              isFabric: isFabricLike,
            })
            if (isFabricLike) {
              configureFabricDiffuseSampling({
                texture: loadedTexture,
                material: newMaterial,
              })
            }
            applyZoneDetailMapToMesh(newMaterial, zoneMaterial, loadedTexture, isFabricLike)

            if (node.material.userData) {
              newMaterial.userData = { ...node.material.userData }
            }

            node.material = newMaterial

            node.material.userData.currentTexture = cleanUrl

            const previousTextureUrl = node.material.userData?.previousTextureUrl
            const urlChanged = cleanUrl !== previousTextureUrl

            if (urlChanged) {
              node.material.userData.previousTextureUrl = cleanUrl
            }
            invalidateR3F()

            return
          } else if (isSpecialCounterTop2) {
            console.log('ENTRA EN specialCounterTop2', itemHeight, itemWidth, itemThickness)
          } else if (isSpecialCounterTop && getContiguousItems && fatherId) {
            console.log('entra en specialCounterTop con contiguos')
            const contiguousResult = analyzeContiguousFurniture(getContiguousItems)
            const group = contiguousResult?.groups.find((g) => g.some((i) => i.id === fatherId))

            if (group) {
              const itemIndex = group.findIndex((i) => i.id === fatherId)

              // CONSTANTES DE REFERENCIA
              const FULL_WIDTH = 3200
              const FULL_HEIGHT = 1200
              const FULL_REPEAT_X = 1
              const FULL_REPEAT_Y = 1

              // Calcular repeat basado en el item actual
              const repeatXCalculated = (itemWidth / FULL_WIDTH) * FULL_REPEAT_X
              const repeatYCalculated = (itemThickness / FULL_HEIGHT) * FULL_REPEAT_Y

              // Calcular ancho acumulado antes de este item
              const accumulatedMm = group
                .slice(0, itemIndex)
                .reduce((acc, item) => acc + item.width, 0)

              // Calcular offset basado en la posición en el grupo
              const offsetFraction = accumulatedMm / FULL_WIDTH
              offsetU = FULL_REPEAT_X * offsetFraction

              // Configurar textura con offset y repeat
              loadedTexture.offset.set(offsetU, 0)
              loadedTexture.repeat.set(repeatXCalculated, repeatYCalculated)
              // loadedTexture.rotation = radians(textureRotation)
              loadedTexture.wrapS = RepeatWrapping
              loadedTexture.wrapT = RepeatWrapping
              loadedTexture.flipY = false
              loadedTexture.needsUpdate = true

              node.material.map = loadedTexture
              node.material.needsUpdate = true
              node.material.userData.currentTexture = cleanUrl
            }
          } else if (isSpecialCounterTop) {
            console.log('entra en specialCounterTop')
            const FULL_WIDTH = 3200
            const FULL_HEIGHT = 1200
            const FULL_REPEAT_X = 1
            const FULL_REPEAT_Y = 1

            const repeatXCalculated = (itemWidth / FULL_WIDTH) * FULL_REPEAT_X
            const repeatYCalculated = (itemThickness / FULL_HEIGHT) * FULL_REPEAT_Y

            if (node.isMesh && node.geometry) {
              const geometry = node.geometry
              const uv = geometry.attributes.uv

              if (uv) {
                // Crear una copia de las UVs originales
                const newUvs = new Float32Array(uv.array.length)

                for (let i = 0; i < uv.count; i++) {
                  const index = i * 2
                  newUvs[index] = uv.array[index] // U se mantiene igual
                  newUvs[index + 1] = 1 - uv.array[index + 1] // V se invierte (1-v)
                }

                // Reemplazar las UVs
                geometry.setAttribute('uv', new THREE.BufferAttribute(newUvs, 2))
                geometry.attributes.uv.needsUpdate = true
              }
            }

            // Configurar la textura normalmente
            loadedTexture.repeat.set(repeatXCalculated, repeatYCalculated)
            loadedTexture.rotation = radians(textureRotation)
            loadedTexture.needsUpdate = true
            loadedTexture.flipY = false
          } else if (isFrontal) {
            // console.log('ENTRA EN FRONTALES', itemHeight, itemWidth, itemThickness)
            const FULL_WIDTH = 3200
            const FULL_HEIGHT = 1200
            const FULL_REPEAT_X = 1
            const FULL_REPEAT_Y = 1

            const repeatXCalculated = (itemWidth / FULL_WIDTH) * FULL_REPEAT_X
            const repeatYCalculated = (itemHeight / FULL_HEIGHT) * FULL_REPEAT_Y
            // Invertir las UVs del objeto directamente (solución más radical)
            if (node.isMesh && node.geometry) {
              // Invertir las UVs manualmente
              const geometry = node.geometry
              const uv = geometry.attributes.uv

              if (uv) {
                // Crear una copia de las UVs originales
                const newUvs = new Float32Array(uv.array.length)

                for (let i = 0; i < uv.count; i++) {
                  const index = i * 2
                  newUvs[index] = uv.array[index] // U se mantiene igual
                  newUvs[index + 1] = 1 - uv.array[index + 1] // V se invierte (1-v)
                }

                // Reemplazar las UVs
                geometry.setAttribute('uv', new THREE.BufferAttribute(newUvs, 2))
                geometry.attributes.uv.needsUpdate = true
              }
            }

            // Configurar la textura normalmente
            loadedTexture.repeat.set(repeatXCalculated, repeatYCalculated)
            loadedTexture.rotation = radians(textureRotation)
            loadedTexture.needsUpdate = true
          } else if (isCountertop && getContiguousItems && fatherId) {
            // Mantener la textura anterior para evitar el parpadeo negro
            const previousTexture = node.material.map
            const previousTextureUrl = node.material.userData.currentTexture

            const contiguousResult = analyzeContiguousFurniture(getContiguousItems)
            const group = contiguousResult?.groups.find((g) => g.some((i) => i.id === fatherId))

            if (group && group.length > 0) {
              const itemIndex = group.findIndex((i) => i.id === fatherId)

              // Verificar que el itemIndex sea válido
              if (itemIndex === -1 || !group[itemIndex]) {
                console.warn(`[handleNodeTextures] Item ${fatherId} no encontrado en el grupo`)
                return
              }

              const item = group[itemIndex]

              // CONSTANTES DE REFERENCIA - NO MODIFICAR
              // Estos valores ya están calibrados para mostrar correctamente la textura en una encimera de 3200x1200mm
              const FULL_WIDTH = 3200 // Ancho de referencia completo (mm)
              const FULL_REPEAT_X = 1

              // Calcular accumulatedMm de forma más precisa y segura
              let accumulatedMm = 0
              for (let i = 0; i < itemIndex; i++) {
                if (group[i] && group[i].width) {
                  accumulatedMm += group[i].width
                }
              }

              // 4. Calcular fracción de desplazamiento según posición en el grupo
              const offsetFraction = accumulatedMm / FULL_WIDTH

              // El offset se calcula proporcionalmente a la posición acumulada
              const offsetU = FULL_REPEAT_X * offsetFraction

              // Si es la misma textura, solo actualizar los parámetros sin reemplazar
              // Comparar usando cleanUrl para evitar problemas con parámetros de query
              if (previousTexture && previousTextureUrl === cleanUrl) {
                // Aplicar los nuevos parámetros a la textura existente
                applyTextureQuality(previousTexture)
                previousTexture.rotation = radians(textureRotation)
                previousTexture.offset.set(offsetU, 0)
                previousTexture.repeat.set(1, 1)
                previousTexture.wrapS = RepeatWrapping
                previousTexture.wrapT = RepeatWrapping
                previousTexture.flipY = false
                previousTexture.needsUpdate = true
                node.material.needsUpdate = true
              } else {
                // Si es una textura diferente, aplicar los parámetros a la nueva textura
                loadedTexture.rotation = radians(textureRotation)
                loadedTexture.offset.set(offsetU, 0)
                loadedTexture.repeat.set(1, 1)
                loadedTexture.wrapS = RepeatWrapping
                loadedTexture.wrapT = RepeatWrapping
                loadedTexture.flipY = false
                loadedTexture.needsUpdate = true

                // Aplicar la nueva textura (ya está cargada en el callback)
                node.material.map = loadedTexture
                node.material.needsUpdate = true
                node.material.userData.currentTexture = cleanUrl
              }
            } else {
              // Si no hay grupo contiguo y hay textura anterior, mantenerla
              // Comparar usando cleanUrl para evitar problemas con parámetros de query
              if (previousTexture && previousTextureUrl === cleanUrl) {
                // Mantener la textura anterior con sus parámetros actuales
                applyTextureQuality(previousTexture)
                node.material.map = previousTexture
                node.material.needsUpdate = true
              } else {
                // Si no hay grupo o es una textura diferente, aplicar la nueva
                node.material.map = loadedTexture
                node.material.needsUpdate = true
                node.material.userData.currentTexture = cleanUrl
              }
            }
          } else if (paginaPdf === 1 || isHorno) {
            // console.log('ENTRA EN LA PÁGINAPDF 1', itemHeight, itemWidth, itemThickness)
            loadedTexture.offset.set(0, 0)
            loadedTexture.rotation = 0
            loadedTexture.repeat.set(1, 1)
            loadedTexture.needsUpdate = true
            node.material.map = loadedTexture
            node.material.needsUpdate = true
            node.material.userData.currentTexture = cleanUrl
          } else {
            const tiling = resolveTextureTiling({
              texture,
              repeatX,
              repeatY,
              textureRotation,
              itemHeight,
              itemId,
            })
            loadedTexture.rotation = tiling.rotation
            loadedTexture.offset.set(tiling.offsetU, tiling.offsetV)
            loadedTexture.repeat.set(tiling.repeatX, tiling.repeatY)
          }
          loadedTexture.needsUpdate = true
          let zoneMaterial = zones[node.name]?.material
          if (itemId) {
            const store = useAppStore.getState()
            const currentItem = store.items.get(itemId) || store.areas.get(itemId)
            const currentZoneMaterial = currentItem?.zones?.[node.name]?.material
            if (currentZoneMaterial) {
              zoneMaterial = currentZoneMaterial
            }
          }

          node.material.map = loadedTexture
          applyZoneMaterialToMesh(node.material, zoneMaterial, {
            hasTextureMap: true,
            isFabric: isFabricLike,
          })
          if (isFabricLike) {
            configureFabricDiffuseSampling({
              texture: loadedTexture,
              material: node.material,
            })
          }
          applyZoneDetailMapToMesh(node.material, zoneMaterial, loadedTexture, isFabricLike)
          node.material.userData.currentTexture = cleanUrl
          invalidateR3F()
        }

        const cachedTexture = cleanUrl ? cloneCachedTexture(cleanUrl) : null
        if (cachedTexture) {
          applyLoadedTexture(cachedTexture)
          newTexture = cachedTexture
        } else {
          newTexture = loader.load(loadUrl, (loadedTexture) => {
            if (cleanUrl) rememberCachedTexture(cleanUrl, loadedTexture)
            applyLoadedTexture(loadedTexture)
          })
        }
      }
    })
  }
  return newTexture
}
export function handleNodeTexturesVentanas(
  node: Node & { name: keyof Zone },
  zones: Zone,
  textures: Textures,
) {
  let newTexture
  if (textures.length > 0) {
    textures?.forEach((texture: Textures[number]) => {
      if (texture.zoneName === node.name) {
        newTexture = loader.load(texture.url + '?not-from-cache-please', (loadedTexture) => {
          loadedTexture.wrapS = RepeatWrapping
          loadedTexture.wrapT = RepeatWrapping
          loadedTexture.flipY = false
          // Mejorar calidad de texturas para renderizado más realista
          applyTextureQuality(loadedTexture)
          loadedTexture.offset.set(0, 0)
          loadedTexture.rotation = 0
          loadedTexture.repeat.set(1, 1)
          loadedTexture.needsUpdate = true
          node.material.map = loadedTexture
          node.material.needsUpdate = true
          node.material.userData.currentTexture = texture.url
        })
      }
      return newTexture
    })
  }
}
